import { useState, FormEvent, useEffect, useRef } from "react";
import ActionModal from "./ActionModal";

interface ExtendGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (turnsToAdd: number) => void;
  isSubmitting?: boolean;
}

export default function ExtendGameModal({ isOpen, onClose, onSubmit, isSubmitting = false }: ExtendGameModalProps) {
  const [turnsToAdd, setTurnsToAdd] = useState<number>(1);
  const [error, setError] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setTurnsToAdd(1);
      setError("");
    }
  }, [isOpen]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (turnsToAdd < 1) {
      setError("Number of turns must be at least 1");
      return;
    }
    if (turnsToAdd > 100) {
      setError("Number of turns cannot exceed 100");
      return;
    }
    onSubmit(turnsToAdd);
    setError("");
  };

  const handleClose = () => {
    setError("");
    setTurnsToAdd(1);
    onClose();
  };

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Extend Game"
    >
      <form onSubmit={handleSubmit} style={{ padding: "1rem 0" }}>
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold", color: "#212529" }}>
            Number of turns to add:
          </label>
          <input
            ref={inputRef}
            type="number"
            min="1"
            max="100"
            value={turnsToAdd}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (!isNaN(value)) {
                setTurnsToAdd(value);
                setError("");
              } else if (e.target.value === "") {
                setTurnsToAdd(1);
                setError("");
              }
            }}
            style={{
              width: "100%",
              padding: "0.5rem",
              fontSize: "1rem",
              border: error ? "1px solid #dc3545" : "1px solid #dee2e6",
              borderRadius: "4px",
              boxSizing: "border-box"
            }}
          />
          {error && (
            <div style={{ color: "#dc3545", fontSize: "0.875rem", marginTop: "0.5rem" }}>
              {error}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              padding: "0.5rem 1rem",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "4px",
              opacity: isSubmitting ? 0.6 : 1
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              padding: "0.5rem 1rem",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              opacity: isSubmitting ? 0.6 : 1
            }}
          >
            {isSubmitting ? "Extending..." : "Extend Game"}
          </button>
        </div>
      </form>
    </ActionModal>
  );
}

